"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ArrowLink from "@/assets/icons/arrow-link.svg";
import ClassicLoader from "@/components/common/classic-loader";
import { FormInput } from "@/components/form";
import { FormCheckbox } from "@/components/form/form-checkbox";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useSubscribe } from "@/features/newsletter/hooks/use-subscribe";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

export const NewsletterForm = ({
  cookie,
  layout = "full",
  className,
  scenario,
}: {
  cookie: string;
  layout?: "inline" | "full";
  className?: string;
  scenario: "newsletter_homepage" | "stopka_newsletter";
}) => {
  const t = useTranslations();
  const { mutate: subscribe } = useSubscribe();
  const [isLoading, setIsLoading] = useState(false);

  const FormSchema = z.object({
    email: z
      .string()
      .min(1, { message: "validators.required" })
      .email("validators.email"),
    consent: z.boolean().refine((val) => val === true, {
      message: "validators.required",
    }),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
      consent: false,
    },
  });

  const onSubmit = (data: z.infer<typeof FormSchema>) => {
    setIsLoading(true);
    subscribe(
      { email: data.email, scenario, cookie },
      {
        onSuccess: () => {
          setIsLoading(false);
          form.reset();
        },
        onError: () => {
          setIsLoading(false);
        },
      },
    );
  };

  const newsletterFormLabel = (
    <Link
      href={{ pathname: "/privacy-policy" }}
      title={t("staticBuilder.consent")}
      className={cn(
        "flex items-center space-x-0.5 text-primary/80 underline hover:no-underline",
        layout === "inline" && "text-base text-brand-black leading-6",
        className,
      )}
      target="_blank"
    >
      <span>{t("staticBuilder.consent")}</span>
      {layout !== "inline" && <ArrowLink />}
    </Link>
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn(
          "flex flex-wrap text-brand-green1-300",
          layout === "inline"
            ? "gap-2 [&_.text-destructive]:text-white"
            : "mt-4 gap-3",
        )}
      >
        <div
          className={cn(
            "order-1 w-full grow",
            layout === "inline" ? "relative" : "md:w-auto",
          )}
        >
          <FormInput
            form={form}
            fieldName="email"
            fieldLabel={t("staticBuilder.email_address")}
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            inputMode="email"
            type="email"
            inputClassName={cn(layout === "inline" && "pr-40")}
          />
          {layout === "inline" && (
            <Button
              type="submit"
              className="absolute top-0 right-0 h-14 bg-brand-green2-300 px-8 text-base text-white hover:bg-brand-green2-300/90 md:order-2 md:w-auto"
              variant="default"
              disabled={isLoading}
            >
              {t("staticBuilder.subscribe")}
              {isLoading && (
                <ClassicLoader className="h-4 w-4 animate-spin md:ml-2" />
              )}
            </Button>
          )}
        </div>
        <div
          className={cn(
            "order-2 md:order-3",
            layout !== "inline" && "mt-1 mb-3 md:mt-4 md:mb-0",
          )}
        >
          <FormCheckbox
            form={form}
            fieldName="consent"
            fieldLabel={newsletterFormLabel}
            layout="bigWhite"
          />
        </div>
        {layout !== "inline" && (
          <Button
            type="submit"
            className="order-3 h-14 w-full px-8 md:order-2 md:w-auto"
            variant="secondary"
            disabled={isLoading}
          >
            {t("staticBuilder.subscribe")}
            {isLoading && (
              <ClassicLoader className="h-4 w-4 animate-spin md:ml-2" />
            )}
          </Button>
        )}
      </form>
    </Form>
  );
};
